import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../../config/firebase';
import { ref, update } from 'firebase/database';

const EmailVerification = () => {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, verifyEmail, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onboardingPreview = location.state?.onboardingPreview || [];
  const redirectTo = location.state?.from?.pathname;
  const selectedCourseName = location.state?.courseName;

  useEffect(() => {
    if (!currentUser) return;

    // Check email verification status every 3 seconds
    const checkVerification = async () => {
      try {
        // Reload the user to get fresh data
        await auth.currentUser.reload();
        const user = auth.currentUser;

        if (user.emailVerified) {
          // Update emailVerified status in Realtime Database
          const userRef = ref(db, `users/${user.uid}`);
          await update(userRef, {
            emailVerified: true,
          });

          navigate(redirectTo || '/student/dashboard');
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };

    const interval = setInterval(checkVerification, 3000);

    // Initial check
    checkVerification();

    return () => clearInterval(interval);
  }, [currentUser, navigate, redirectTo]);

  const handleResendVerification = async () => {
    try {
      setMessage('');
      setError('');
      setLoading(true);
      await verifyEmail();
      setMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      setError('Failed to resend verification email. ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = async () => {
    try {
      await logout();
      navigate('/login', {
        state: {
          from: location.state?.from,
          courseName: selectedCourseName,
        },
      });
    } catch (error) {
      setError('Failed to log out. ' + error.message);
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  // If email is already verified, redirect to dashboard
  if (auth.currentUser?.emailVerified || currentUser.emailVerified) {
    navigate(redirectTo || '/student/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl w-full grid md:grid-cols-2 gap-8 items-start">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a verification email to {currentUser.email}
          </p>
          {selectedCourseName ? (
            <p className="mt-2 text-center text-sm text-primary-700">
              After verification, you will continue with {selectedCourseName}.
            </p>
          ) : null}
        </div>

        {error && (
          <div className="bg-red-50 p-4 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {message && (
          <div className="bg-green-50 p-4 rounded-md">
            <p className="text-sm text-green-700">{message}</p>
          </div>
        )}

        <div className="space-y-4">
          <p className="text-center text-sm text-gray-600">
            Didn't receive the email? Check your spam folder or click below to
            resend.
          </p>
          <button
            onClick={handleResendVerification}
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {loading ? 'Sending...' : 'Resend Verification Email'}
          </button>
          <button
            onClick={handleBackToSignIn}
            className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Sign In
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <p className="text-sm uppercase tracking-wide text-blue-600 font-semibold">
            Guided Onboarding
          </p>
          <h3 className="text-xl font-semibold text-gray-900 mt-2">
            Your student journey starts right after verification
          </h3>
          <div className="mt-4 space-y-3">
            {(onboardingPreview.length
              ? onboardingPreview
              : [
                  'Verify your email',
                  'Complete your profile',
                  'Choose your first course',
                  'Submit your enrollment',
                  'Start your first lesson',
                ]
            ).map((item) => (
              <div
                key={item}
                className="flex items-center justify-between border rounded-lg px-3 py-2"
              >
                <span className="text-sm text-gray-700">{item}</span>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                  Upcoming
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
